import Text from '@atoms/Text/Text';
import { Tiers } from '@models/AnimatedTier';
import useMediaQuery from '@utility/hooks/useMediaQuery';
import classNames from 'classnames';
import { motion, useInView } from 'motion/react';
import { useRef } from 'react';

interface Props {
  readonly tiers: Tiers;
}

const AnimatedTier = ({ tiers }: Props) => {
  const isMobile = useMediaQuery(1408);
  const containerRef = useRef(null);
  const inView = useInView(containerRef, { once: true });

  const tierProgressionWidths = {
    BRONZE: 30,
    SILVER: 62,
    GOLD: 100,
  };

  const tierSplitPositionsByPercentage = [2, 35, 68, 98];
  const activeTierIndex = tiers.findIndex(({ isActive }) => isActive);

  const activeTierPercentage =
    tierProgressionWidths[tiers[activeTierIndex]!.tier];

  const handleSeperatorPosition = (tierPosition: number, index: number) => {
    if (isMobile) return `${tierPosition}%`;

    switch (true) {
      case index === 0:
        return `${tierPosition + 1}%`;
      case index === 3:
        return `${tierPosition - 1}%`;
      default:
        return `${tierPosition}%`;
    }
  };

  return (
    <div className="tw-text-black" ref={containerRef}>
      <div className="tw-h-2 tw-w-full tw-rounded-[40px] tw-bg-white/20">
        <motion.div
          className="tw-h-2 tw-w-0 tw-rounded-[40px] tw-bg-yellow"
          initial={{ width: 0 }}
          animate={inView ? { width: `${activeTierPercentage}%` } : {}}
          transition={{ ease: [0.25, 1, 0.99, 1], duration: 1, delay: 0.5 }}
        />
      </div>
      <div className="tw-relative tw-mb-6 tw-mt-2 tw-w-full xl:tw-mb-4">
        {tierSplitPositionsByPercentage.map((tierPosition, index) => (
          <span
            key={tierPosition}
            className="tw-absolute tw-block tw-h-2 tw-w-[1px] tw-bg-white/20"
            style={{ left: handleSeperatorPosition(tierPosition, index) }}
          />
        ))}
      </div>
      <div className="tw-grid tw-grid-cols-3 tw-justify-between tw-gap-x-5 tw-px-1 md:tw-px-2.5">
        {tiers.map(({ tier }, index) => (
          <Text
            key={tier}
            as="p"
            variant="label-12"
            className={classNames(
              'tw-break-all tw-text-white md:tw-text-center',
              {
                'tw-text-left': index === 0,
                'tw-text-center': index === 1,
                'tw-text-right': index === 2,
              },
            )}
          >
            {tier}
          </Text>
        ))}
      </div>
    </div>
  );
};

export default AnimatedTier;
